import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Truck, Plus, X, ArrowRight, CheckCircle2, Ban } from "lucide-react";

const STATUS_MAP = { pending: "draft", in_transit: "in_progress", received: "completed", cancelled: "blocked" };

export default function Transfers() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, wh, tr] = await Promise.all([
        api.get("/inventory?limit=2000"),
        api.get("/transfers/warehouses"),
        api.get("/transfers"),
      ]);
      setItems(inv.data.items || []);
      setWarehouses(wh.data.warehouses || ["Main"]);
      setTransfers(tr.data.transfers || []);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? transfers : transfers.filter((t) => t.status === filter);

  const receive = async (t) => {
    try { await api.post(`/transfers/${t.id}/receive`); toast.success("Received — stock moved"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };
  const cancel = async (t) => {
    if (!window.confirm("Cancel this transfer? In-transit stock will be restored.")) return;
    try { await api.delete(`/transfers/${t.id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <AppShell title="Warehouse Transfers" subtitle={`${transfers.length} transfers`}
      headerActions={<Button size="sm" onClick={() => setOpen(true)} data-testid="transfer-create-button"><Plus className="w-4 h-4 mr-1.5" />New transfer</Button>}>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" variant={filter === "all" ? "default" : "ghost"} onClick={() => setFilter("all")}>All</Button>
        {["in_transit", "received", "cancelled"].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "ghost"} onClick={() => setFilter(s)} className="capitalize" data-testid={`transfer-filter-${s}`}>
            {s.replace("_", " ")} ({transfers.filter((t) => t.status === s).length})
          </Button>
        ))}
      </div>
      {loading ? <Skeleton className="h-40" /> :
        filtered.length === 0 ? (
          <EmptyState icon={Truck} title="No transfers yet" description="Move stock between warehouses. Source is deducted immediately; destination receives on confirmation."
            action={<Button onClick={() => setOpen(true)} data-testid="transfer-empty-create"><Plus className="w-4 h-4 mr-1.5" />New transfer</Button>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((t) => (
              <div key={t.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`transfer-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-mono text-muted-foreground">{t.transfer_number}</div>
                    <div className="font-heading font-semibold text-lg flex items-center gap-2">{t.from_warehouse} <ArrowRight className="w-4 h-4 text-muted-foreground" /> {t.to_warehouse}</div>
                  </div>
                  <StatusBadge status={STATUS_MAP[t.status] || "draft"} />
                </div>
                <div className="mt-3 space-y-1 max-h-28 overflow-y-auto">
                  {(t.lines || []).map((l, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border border-border rounded px-2 py-1">
                      <span className="truncate">{l.item_name}{l.lot_number ? ` · Lot ${l.lot_number}` : ""}</span>
                      <span className="font-mono tabular-nums">x{l.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between pt-2 border-t border-border gap-2">
                  <div className="text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</div>
                  <div className="flex items-center gap-1">
                    {t.status === "in_transit" && <Button size="sm" onClick={() => receive(t)} data-testid={`transfer-receive-${t.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Receive</Button>}
                    {t.status === "in_transit" && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(t)} data-testid={`transfer-cancel-${t.id}`}><Ban className="w-3.5 h-3.5" /></Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      {open && <TransferForm open={open} onClose={() => setOpen(false)} onCreated={load} items={items} warehouses={warehouses} />}
    </AppShell>
  );
}

function TransferForm({ open, onClose, onCreated, items, warehouses }) {
  const [form, setForm] = useState({ from_warehouse: warehouses[0] || "Main", to_warehouse: "", notes: "", lines: [{ item_id: "", quantity: 1, lot_number: "" }] });
  const [customTo, setCustomTo] = useState("");
  const [sub, setSub] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const to = form.to_warehouse === "__custom__" ? customTo.trim() : form.to_warehouse;
    if (!to) { toast.error("Destination warehouse required"); return; }
    if (to === form.from_warehouse) { toast.error("From/To must differ"); return; }
    if (form.lines.some((l) => !l.item_id || Number(l.quantity) <= 0)) { toast.error("Each line needs item + qty"); return; }
    setSub(true);
    try {
      await api.post("/transfers", {
        ...form, to_warehouse: to,
        lines: form.lines.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity), lot_number: l.lot_number || null })),
      });
      toast.success("Transfer created — stock in transit");
      onCreated(); onClose();
    } catch (e) { toast.error(extractErr(e)); }
    setSub(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New warehouse transfer</DialogTitle><DialogDescription>Source stock is deducted immediately. Destination receives on confirmation.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>From warehouse</Label>
              <Select value={form.from_warehouse} onValueChange={(v) => setForm((p) => ({ ...p, from_warehouse: v }))}>
                <SelectTrigger data-testid="transfer-form-from"><SelectValue /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>To warehouse</Label>
              <Select value={form.to_warehouse} onValueChange={(v) => setForm((p) => ({ ...p, to_warehouse: v }))}>
                <SelectTrigger data-testid="transfer-form-to"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.filter((w) => w !== form.from_warehouse).map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}<SelectItem value="__custom__">+ New warehouse...</SelectItem></SelectContent>
              </Select>
              {form.to_warehouse === "__custom__" && <Input className="mt-2" placeholder="New warehouse name" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="transfer-form-to-custom" />}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label>Items</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", quantity: 1, lot_number: "" }] }))}><Plus className="w-4 h-4 mr-1" />Add</Button>
            </div>
            <div className="mt-2 space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border rounded p-2">
                  <div className="col-span-7 space-y-1"><Label className="text-[11px]">Item</Label>
                    <Select value={l.item_id} onValueChange={(v) => { const c = [...form.lines]; c[i].item_id = v; setForm((f) => ({ ...f, lines: c })); }}>
                      <SelectTrigger data-testid={`transfer-form-line-${i}-item`}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="max-h-[300px]">{items.map((p) => <SelectItem key={p.id} value={p.id}><span className="font-mono text-xs mr-2">{p.sku}</span>{p.name} <span className="text-muted-foreground">({p.quantity})</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-1"><Label className="text-[11px]">Qty</Label><Input type="number" step="any" min="0" value={l.quantity} onChange={(e) => { const c = [...form.lines]; c[i].quantity = e.target.value; setForm((f) => ({ ...f, lines: c })); }} data-testid={`transfer-form-line-${i}-qty`} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[11px]">Lot (optional)</Label><Input value={l.lot_number} onChange={(e) => { const c = [...form.lines]; c[i].lot_number = e.target.value; setForm((f) => ({ ...f, lines: c })); }} /></div>
                  <div className="col-span-1 flex justify-end">{form.lines.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))}><X className="w-4 h-4" /></Button>}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={sub} data-testid="transfer-form-submit">{sub ? "Creating…" : "Create transfer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
